import type { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { EDIT_MAX_SIDE } from '../editor/photo';
import { naturalSize, swapSrc } from '../editor/picture';
import { assets } from '../editor/storage';
import { api } from '../sync/api';
import { toast } from './toast';

/*
 * "ลบพื้นหลัง" (แต่งภาพ round 3, choice ข: on the group's own VM through the Worker — vps/bg-remover).
 * The picture's file is sent; the cut-out comes back the same size and replaces the file in the picture (choice ก),
 * so its place, crop, shape and แต่งภาพ look stay. The original file's address is kept as `bgOriginal` (EXTRA_PROPS):
 * "คืนพื้นหลัง" puts it back, and undo works as for any change. Every failure is shown with its reason.
 */

type Pic = fabric.Image & { bgOriginal?: string };
const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);
const TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** the same picture at two addresses (Fabric gives full URLs; the server's are paths) */
const samePath = (a: string, b: string) => { try { return new URL(a, location.href).href === new URL(b, location.href).href; } catch { return a === b; } };

export const ICON_BG = '<svg viewBox="0 0 24 24"><path d="M12 3c-3 0-5 2.2-5 5 0 2 1.2 3.4 2.4 4.2L8 21h8l-1.4-8.8C15.8 11.4 17 10 17 8c0-2.8-2-5-5-5z"/><path d="M3 3h3M3 3v3M21 3h-3M21 3v3M3 21h3M3 21v-3M21 21h-3M21 21v-3"/></svg>';

export function initBgRemove(ed: Editor, history: History, refresh: () => void) {
  const { canvas } = ed;
  const busy = new WeakSet<fabric.Object>();
  const onPage = (o: fabric.Object) => canvas.getObjects().includes(o);
  /** draw the chips again only when this picture is the one chosen (never under a finger on another chip) */
  const redraw = (o: fabric.Object) => { if (canvas.getActiveObject() === o) refresh(); };

  /** the picture's own file: kept on this device (not uploaded yet) or on the server */
  async function fileOf(img: Pic): Promise<Blob> {
    const src = img.getSrc();
    if (!src) throw new Error('รูปนี้ไม่มีไฟล์ (โหลดไม่สำเร็จ)');
    const own = assets.get(src);
    if (own) return own;
    let r: Response;
    try { r = await fetch(src, { credentials: 'same-origin' }); }
    catch { throw new Error('โหลดไฟล์รูปไม่ได้ (ไม่มีอินเทอร์เน็ต)'); }
    if (!r.ok) throw new Error(`โหลดไฟล์รูปไม่ได้ (HTTP ${r.status})`);
    return r.blob();
  }

  async function removeBg(img: Pic) {
    if (busy.has(img)) return;
    busy.add(img); redraw(img);
    toast('กำลังลบพื้นหลัง… ส่งรูปไปที่เครื่องของกลุ่ม ใช้เวลาประมาณ 5–60 วินาที ทำงานอื่นต่อได้');
    try {
      // as big as the VM takes (4096 × 4096): bigger ones are made smaller by แต่งภาพ first (nothing is used up)
      const nat = naturalSize(img);
      if (nat.w * nat.h > EDIT_MAX_SIDE * EDIT_MAX_SIDE) {
        throw new Error(`รูปใหญ่ ${nat.w}×${nat.h} px เกินที่เครื่องลบพื้นหลังรับ (${EDIT_MAX_SIDE}×${EDIT_MAX_SIDE}) กด "แต่งภาพ" ที่รูปก่อน ระบบจะย่อให้อัตโนมัติ แล้วลองใหม่`);
      }
      const from = img.getSrc();
      const file = await fileOf(img);
      const type = (file.type || '').toLowerCase();
      if (!TYPES.includes(type)) throw new Error(`ไฟล์รูปชนิด "${type || 'ไม่ทราบ'}" ส่งลบพื้นหลังไม่ได้ (รับ JPG, PNG, WebP)`);
      const r = await api.bgRemove(file);
      if (!onPage(img)) { toast('ลบพื้นหลังเสร็จแล้ว แต่รูปนี้ไม่อยู่ในหน้านี้แล้ว (ย้อนกลับหรือเปลี่ยนหน้าระหว่างรอ) จึงไม่ได้ใส่ผลให้', true); return; }
      // a new picture is uploaded by the autosave meanwhile (blob: → the server copy, the same file as r.originalUrl):
      // still the picture that was sent (BUG-35)
      const now = img.getSrc();
      const sent = now === from || samePath(now, r.originalUrl) ? now : from;
      // the cut-out must be the same size: the crop and the place on the page stay exactly as they are
      const swapped = await swapSrc(img, r.url, 'เปิดรูปที่ลบพื้นหลังแล้วไม่ได้', 1, sent, true);
      if (!swapped) { toast('รูปนี้ถูกเปลี่ยนไฟล์ระหว่างลบพื้นหลัง (เช่น แต่งภาพย่อรูป) จึงไม่ได้ใส่ผล กด "ลบพื้นหลัง" อีกครั้ง', true); return; }
      img.set('bgOriginal' as keyof fabric.Object, img.bgOriginal || r.originalUrl);
      canvas.requestRenderAll();
      history.push();
      toast(`ลบพื้นหลังแล้ว (${(r.ms / 1000).toFixed(1)} วินาที · วันนี้ใช้ ${r.used}/${r.limit} รูป) กด "คืนพื้นหลัง" ได้ทุกเมื่อ`);
    } catch (e) {
      toast(`ลบพื้นหลังไม่สำเร็จ: ${errText(e)}`, true);
    } finally {
      busy.delete(img); redraw(img);
    }
  }

  async function restore(img: Pic) {
    const orig = img.bgOriginal;
    if (!orig || busy.has(img)) return;
    busy.add(img); redraw(img);
    try {
      const swapped = await swapSrc(img, orig, 'เปิดรูปเดิม (ก่อนลบพื้นหลัง) ไม่ได้', 1, img.getSrc(), true);
      if (!swapped) { toast('รูปนี้ถูกเปลี่ยนไฟล์ระหว่างนั้น ลองกด "คืนพื้นหลัง" อีกครั้ง', true); return; }
      img.set('bgOriginal' as keyof fabric.Object, undefined);
      canvas.requestRenderAll();
      history.push();
      toast('คืนพื้นหลังแล้ว (รูปเดิม)');
    } catch (e) {
      toast(`คืนพื้นหลังไม่สำเร็จ: ${errText(e)}`, true);
    } finally {
      busy.delete(img); redraw(img);
    }
  }

  /** the chip for a picture: ลบพื้นหลัง, or คืนพื้นหลัง after it was removed; off with the reason while working */
  function chip(o: Pic): { act: () => void; label: string; html: string; off?: string } {
    const working = busy.has(o);
    if (o.bgOriginal) {
      return { act: () => void restore(o), label: 'คืนพื้นหลัง', html: `${ICON_BG}<span>${working ? 'กำลังทำ…' : 'คืนพื้นหลัง'}</span>`, off: working ? 'กำลังทำอยู่ รอสักครู่' : undefined };
    }
    return { act: () => void removeBg(o), label: 'ลบพื้นหลัง', html: `${ICON_BG}<span>${working ? 'กำลังลบ…' : 'ลบพื้นหลัง'}</span>`, off: working ? 'กำลังลบพื้นหลังรูปนี้อยู่ รอสักครู่' : undefined };
  }

  return { chip, removeBg, restore };
}
